import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  addTeamMember,
  deleteTeamMember,
  getAllTeamMembers,
  updateTeamMember as updateTeamMemberApi,
  uploadImage as uploadTeamImage,
} from "../../../../../services/teamServices";

import {
  createUser as createUserApi,
  deleteUser,
  getAllUsers,
  updateUser as updateUserApi,
  uploadImage as uploadUserImage,
} from "../../../../../services/userServices";

import {
  getSettings,
  updateSettings,
} from "../../../../../services/settingsServices";

import {
  buildClubStats,
  filterAndSortTeamMembers,
  filterAndSortUsers,
  isDemoUser,
  normalizeHistoryPayload,
  normalizeTeamMemberPayload,
  normalizeUserPayload,
  safeJsonParse,
} from "../utils/leClubUtils";

const INITIAL_FILTERS = {
  team: {
    query: "",
    category: "all",
    sortOrder: "position_asc",
  },
  users: {
    query: "",
    role: "all",
    sortOrder: "name_asc",
  },
};

const INITIAL_LOADING = {
  team: true,
  users: true,
  settings: true,
};

export default function useLeClub() {
  const [activeTab, setActiveTab] = useState("team");

  const [teamMembers, setTeamMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);

  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [loading, setLoading] = useState(INITIAL_LOADING);
  const [saving, setSaving] = useState(false);

  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const setFilter = useCallback((section, name, value) => {
    setFilters((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [name]: value,
      },
    }));
  }, []);

  const patchLoading = useCallback((key, value) => {
    setLoading((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const loadUserMode = useCallback(() => {
    const user =
      safeJsonParse(localStorage.getItem("user"), null) ||
      safeJsonParse(localStorage.getItem("userInfo"), null);

    setIsDemoMode(isDemoUser(user));
  }, []);

  const guardDemoMode = useCallback(() => {
    if (!isDemoMode) return false;

    toast.info("Fonction désactivée en mode démonstration.");
    return true;
  }, [isDemoMode]);

  const refreshTeamMembers = useCallback(async () => {
    patchLoading("team", true);

    try {
      const data = await getAllTeamMembers();
      setTeamMembers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération de l'équipe :", error);
      toast.error("Erreur lors de la récupération de l'équipe du club.");
    } finally {
      patchLoading("team", false);
    }
  }, [patchLoading]);

  const refreshUsers = useCallback(async () => {
    patchLoading("users", true);

    try {
      const data = await getAllUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs :", error);
      toast.error("Erreur lors de la récupération des utilisateurs.");
    } finally {
      patchLoading("users", false);
    }
  }, [patchLoading]);

  const refreshSettings = useCallback(async () => {
    patchLoading("settings", true);

    try {
      const data = await getSettings();
      const firstSettings = Array.isArray(data) ? data[0] : data;

      setSettings(firstSettings || null);
    } catch (error) {
      console.error("Erreur lors de la récupération de l'historique :", error);
      toast.error("Erreur lors de la récupération de l'historique du club.");
    } finally {
      patchLoading("settings", false);
    }
  }, [patchLoading]);

  const refreshAll = useCallback(async () => {
    await Promise.all([
      refreshTeamMembers(),
      refreshUsers(),
      refreshSettings(),
    ]);
  }, [refreshSettings, refreshTeamMembers, refreshUsers]);

  useEffect(() => {
    loadUserMode();
    refreshAll();
  }, [loadUserMode, refreshAll]);

  const uploadImage = useCallback(async ({ imageFile, category, type }) => {
    if (!imageFile) return null;

    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("category", category);

    const serviceUpload =
      type === "user" ? uploadUserImage : uploadTeamImage;

    const response = await serviceUpload(formData);

    return response?.filePath || response?.imageUrl || response?.url || null;
  }, []);

  const filteredTeamMembers = useMemo(
    () => filterAndSortTeamMembers(teamMembers, filters.team),
    [filters.team, teamMembers]
  );

  const filteredUsers = useMemo(
    () => filterAndSortUsers(users, filters.users),
    [filters.users, users]
  );

  const stats = useMemo(
    () => buildClubStats(teamMembers, users, settings),
    [settings, teamMembers, users]
  );

  const closeDrawer = useCallback(() => {
    setDrawerMode(null);
    setSelectedTeamMember(null);
    setSelectedUser(null);
  }, []);

  const openCreateTeamDrawer = useCallback(() => {
    if (guardDemoMode()) return;

    setSelectedTeamMember(null);
    setDrawerMode("team-create");
  }, [guardDemoMode]);

  const openEditTeamDrawer = useCallback(
    (member) => {
      if (guardDemoMode()) return;

      setSelectedTeamMember(member);
      setDrawerMode("team-edit");
    },
    [guardDemoMode]
  );

  const openTeamDetailsDrawer = useCallback((member) => {
    setSelectedTeamMember(member);
    setDrawerMode("team-details");
  }, []);

  const openCreateUserDrawer = useCallback(() => {
    if (guardDemoMode()) return;

    setSelectedUser(null);
    setDrawerMode("user-create");
  }, [guardDemoMode]);

  const openEditUserDrawer = useCallback(
    (user) => {
      if (guardDemoMode()) return;

      setSelectedUser(user);
      setDrawerMode("user-edit");
    },
    [guardDemoMode]
  );

  const openUserDetailsDrawer = useCallback((user) => {
    setSelectedUser(user);
    setDrawerMode("user-details");
  }, []);

  const openHistoryDrawer = useCallback(() => {
    if (guardDemoMode()) return;

    setDrawerMode("history-edit");
  }, [guardDemoMode]);

  const createTeamMember = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = normalizeTeamMemberPayload(formData);

        if (formData.imageFile) {
          const uploadedUrl = await uploadImage({
            imageFile: formData.imageFile,
            category: "teams",
            type: "team",
          });

          if (uploadedUrl) payload.photoURL = uploadedUrl;
        }

        delete payload.imageFile;

        await addTeamMember(payload);

        toast.success("Membre ajouté avec succès.");
        closeDrawer();
        await refreshTeamMembers();
      } catch (error) {
        console.error("Erreur lors de l'ajout du membre :", error);
        toast.error("Erreur lors de l'ajout du membre.");
      } finally {
        setSaving(false);
      }
    },
    [closeDrawer, guardDemoMode, refreshTeamMembers, uploadImage]
  );

  const updateTeamMember = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = normalizeTeamMemberPayload(formData);

        if (formData.imageFile) {
          const uploadedUrl = await uploadImage({
            imageFile: formData.imageFile,
            category: "teams",
            type: "team",
          });

          if (uploadedUrl) payload.photoURL = uploadedUrl;
        }

        delete payload.imageFile;

        await updateTeamMemberApi(payload.id, payload);

        toast.success("Membre modifié avec succès.");
        closeDrawer();
        await refreshTeamMembers();
      } catch (error) {
        console.error("Erreur lors de la modification du membre :", error);
        toast.error("Erreur lors de la modification du membre.");
      } finally {
        setSaving(false);
      }
    },
    [closeDrawer, guardDemoMode, refreshTeamMembers, uploadImage]
  );

  const createUser = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = normalizeUserPayload(formData, "create");

        if (formData.imageFile) {
          const uploadedUrl = await uploadImage({
            imageFile: formData.imageFile,
            category: "users",
            type: "user",
          });

          if (uploadedUrl) payload.photoURL = uploadedUrl;
        }

        delete payload.imageFile;

        await createUserApi(payload);

        toast.success("Utilisateur ajouté avec succès.");
        closeDrawer();
        await refreshUsers();
      } catch (error) {
        console.error("Erreur lors de l'ajout de l'utilisateur :", error);
        toast.error("Erreur lors de l'ajout de l'utilisateur.");
      } finally {
        setSaving(false);
      }
    },
    [closeDrawer, guardDemoMode, refreshUsers, uploadImage]
  );

  const updateUser = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = normalizeUserPayload(formData, "edit");

        if (formData.imageFile) {
          const uploadedUrl = await uploadImage({
            imageFile: formData.imageFile,
            category: "users",
            type: "user",
          });

          if (uploadedUrl) payload.photoURL = uploadedUrl;
        }

        delete payload.imageFile;

        await updateUserApi(payload.id, payload);

        toast.success("Utilisateur modifié avec succès.");
        closeDrawer();
        await refreshUsers();
      } catch (error) {
        console.error("Erreur lors de la modification de l'utilisateur :", error);
        toast.error("Erreur lors de la modification de l'utilisateur.");
      } finally {
        setSaving(false);
      }
    },
    [closeDrawer, guardDemoMode, refreshUsers, uploadImage]
  );

  const updateHistory = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = normalizeHistoryPayload(formData);

        await updateSettings(payload);

        toast.success("Historique du club mis à jour.");
        closeDrawer();
        await refreshSettings();
      } catch (error) {
        console.error("Erreur lors de la mise à jour de l'historique :", error);
        toast.error("Erreur lors de la mise à jour de l'historique.");
      } finally {
        setSaving(false);
      }
    },
    [closeDrawer, guardDemoMode, refreshSettings]
  );

  const askDeleteTeamMember = useCallback(
    (memberId) => {
      if (guardDemoMode()) return;

      setDeleteCandidate({
        type: "team",
        id: memberId,
      });
    },
    [guardDemoMode]
  );

  const askDeleteUser = useCallback(
    (userId) => {
      if (guardDemoMode()) return;

      setDeleteCandidate({
        type: "user",
        id: userId,
      });
    },
    [guardDemoMode]
  );

  const cancelDelete = useCallback(() => {
    setDeleteCandidate(null);
  }, []);

  const confirmDeleteTeamMember = useCallback(
    async (memberId) => {
      if (guardDemoMode()) return;

      try {
        await deleteTeamMember(memberId);

        toast.success("Membre supprimé avec succès.");
        setDeleteCandidate(null);
        await refreshTeamMembers();
      } catch (error) {
        console.error("Erreur lors de la suppression du membre :", error);
        toast.error("Erreur lors de la suppression du membre.");
      }
    },
    [guardDemoMode, refreshTeamMembers]
  );

  const confirmDeleteUser = useCallback(
    async (userId) => {
      if (guardDemoMode()) return;

      try {
        await deleteUser(userId);

        toast.success("Utilisateur supprimé avec succès.");
        setDeleteCandidate(null);
        await refreshUsers();
      } catch (error) {
        console.error("Erreur lors de la suppression de l'utilisateur :", error);
        toast.error("Erreur lors de la suppression de l'utilisateur.");
      }
    },
    [guardDemoMode, refreshUsers]
  );

  return {
    activeTab,
    setActiveTab,
    loading,
    saving,
    filters,
    setFilter,
    teamMembers,
    filteredTeamMembers,
    users,
    filteredUsers,
    settings,
    stats,
    drawerMode,
    selectedTeamMember,
    selectedUser,
    deleteCandidate,
    actions: {
      refreshAll,
      refreshTeamMembers,
      refreshUsers,
      refreshSettings,
      closeDrawer,
      openCreateTeamDrawer,
      openEditTeamDrawer,
      openTeamDetailsDrawer,
      openCreateUserDrawer,
      openEditUserDrawer,
      openUserDetailsDrawer,
      openHistoryDrawer,
      createTeamMember,
      updateTeamMember,
      createUser,
      updateUser,
      updateHistory,
      askDeleteTeamMember,
      askDeleteUser,
      cancelDelete,
      confirmDeleteTeamMember,
      confirmDeleteUser,
    },
  };
}